import type { DiscountType, InvoiceLine, InvoiceLineTaxDetail } from '@/types/invoice'
import type { LineTax, RenditionInvoice, RenditionLine } from '@/domain/rendition/Rendition'
import {
  initWithholdingBaseTracking,
  recalcLineDerived,
  recalcLineTotal,
  type TrackableWithholding,
} from '@/utils/invoiceLineRecalc'
import { getDiscountAmount, getLineNet, isValueDiscount } from '@/utils/invoiceLineNet'

// A rendition line runs on the payment invoice form's math (`utils/invoiceLineRecalc`), so its net, taxes,
// withholdings and `total` (net + taxes − withholdings) mean what they mean everywhere else.

/** What that math needs besides the line: the invoice's discount mode, the currency decimals and the country. */
export interface LineMathContext {
  discountType?: DiscountType
  decimals: number
  country?: string | null
}

type PaymentRow = InvoiceLineTaxDetail | TrackableWithholding

interface PaymentLine {
  line: InvoiceLine
  // Each rendition tax row next to the payment row standing in for it, to copy the results back.
  rows: [LineTax, PaymentRow][]
}

function toPaymentLine(line: RenditionLine): PaymentLine {
  const rows: [LineTax, PaymentRow][] = []
  const taxDetails = line.taxes.filter((t) => t.taxTypeCode === 'TAX').map((t) => {
    const row: InvoiceLineTaxDetail = {
      tax_type: t.fiscalCode ?? '',
      tax_name: t.taxName,
      tax_type_code: t.fiscalCode ?? '',
      percentage: t.taxPercentage,
      taxable_amount: t.taxableAmount,
      tax_amount: t.taxAmount,
    }
    rows.push([t, row])
    return row
  })
  const withholdings = line.taxes.filter((t) => t.taxTypeCode === 'WITHHOLDING').map((t, i) => {
    const row: TrackableWithholding = {
      id: String(t.id ?? `w-${i}`),
      tax_type: t.fiscalCode || t.taxName,
      tax_type_code: t.fiscalCode,
      percentage: t.taxPercentage,
      taxable_amount: t.taxableAmount,
      tax_amount: t.taxAmount,
      _baseTracksNet: t.baseTracksNet,
    }
    rows.push([t, row])
    return row
  })
  return {
    line: {
      number: line.number ?? 0,
      product: line.itemName,
      product_code: line.productCode ?? '',
      description: line.description ?? '',
      unit_price: line.unitPrice,
      quantity: line.quantity,
      discount: line.discount,
      total: line.total,
      tax_details: taxDetails,
      withholdings,
      splits: [],
    },
    rows,
  }
}

function applyPaymentLine(line: RenditionLine, payment: PaymentLine): void {
  line.unitPrice = payment.line.unit_price ?? 0
  line.total = payment.line.total ?? 0
  payment.rows.forEach(([tax, row]) => {
    tax.taxableAmount = row.taxable_amount ?? 0
    tax.taxAmount = row.tax_amount ?? 0
    if ('_baseTracksNet' in row) tax.baseTracksNet = row._baseTracksNet
  })
}

/** The line's net: price × quantity less its discount, read in the invoice's discount mode. Never `total`. */
export function lineNet(line: Pick<RenditionLine, 'unitPrice' | 'quantity' | 'discount'>, discountType?: DiscountType): number {
  return getLineNet({ unit_price: line.unitPrice, quantity: line.quantity, discount: line.discount }, discountType)
}

/** A percentage discount written in the invoice's discount mode: the percentage itself, or the amount it takes off. */
export function discountInMode(line: Pick<RenditionLine, 'unitPrice' | 'quantity'>, pct: number, discountType?: DiscountType): number {
  if (!isValueDiscount(discountType)) return pct
  return getDiscountAmount({ unit_price: line.unitPrice, quantity: line.quantity, discount: pct }, 'PERCENTAGE')
}

/** The invoice's lines in the payment shape, for the shared totals (`calculateInvoiceTotals`). */
export function paymentLinesOf(inv: Pick<RenditionInvoice, 'lines'>): InvoiceLine[] {
  return inv.lines.map((line) => toPaymentLine(line).line)
}

/** What the line charges from its own amounts (net + taxes − withholdings), without touching it. */
export function lineChargedTotal(line: RenditionLine, discountType?: DiscountType): number {
  const payment = toPaymentLine(line)
  recalcLineTotal(payment.line, discountType)
  return payment.line.total ?? 0
}

/**
 * Prepare a line read from the API: decide which withholding bases track (as the payment form does on load)
 * and rebuild `total` from its own amounts, since the stored total is a net or a gross depending on the origin.
 */
export function loadLine(line: RenditionLine, ctx: LineMathContext): void {
  const payment = toPaymentLine(line)
  initWithholdingBaseTracking(payment.line, ctx.decimals, ctx.discountType, ctx.country)
  recalcLineTotal(payment.line, ctx.discountType, ctx.country)
  applyPaymentLine(line, payment)
}

/**
 * Recompute a line after an edit. `netChanged` (price, quantity or discount) snaps tax bases back to the net;
 * a tax-only edit keeps a base the user typed. Same rule as the payment invoice form.
 */
export function recalcLine(line: RenditionLine, ctx: LineMathContext, netChanged: boolean): void {
  const payment = toPaymentLine(line)
  recalcLineDerived(payment.line, ctx.decimals, { resetTaxBaseToNet: netChanged, discountType: ctx.discountType, country: ctx.country })
  applyPaymentLine(line, payment)
}

